import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {InvertSockets} from "#material/nodes/color/invert/definition"
import {invertDefinition} from "#material/nodes/color/invert/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeConvert, threeRGBColor, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import {cyclesColor} from "#material/cycles/helpers"
import type {ThreeReturns} from "#material/sockets/three"
import type {ColorNode, InvertNode} from "#material/cycles/types"

@registerMaterialGraphNode
export class Invert extends MaterialNode<InvertSockets> {
    public override get definition() {
        return invertDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<InvertSockets>> => {
        if (this.parameters.ui?.disabled) {
            return {
                color:
                    (await evaluator.get(this.parameters.inputs.color))?.toColor() ??
                    threeConvert(this.parameters.settings.color, threeRGBColor) ??
                    threeRGBColor({
                        r: 0,
                        g: 0,
                        b: 0,
                    }),
            }
        }

        const {color, fac} = await evaluator.getAll({color: this.parameters.inputs.color, fac: this.parameters.inputs.fac})

        const colorValue = color?.toColor() ?? threeConvert(this.parameters.settings.color, threeRGBColor) ?? threeRGBColor({r: 0, g: 0, b: 0})
        const facValue = fac?.toFloat() ?? threeConvert(this.parameters.settings.fac, threeValue) ?? threeValue(1)
        const inverted = TSL.sub(threeValue(1), colorValue.rgb)
        return {color: TSL.mix(colorValue, inverted, facValue).toColor()}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<InvertSockets>> => {
        if (this.parameters.ui?.disabled) {
            const inputColor = await evaluator.get(this.parameters.inputs.color)
            if (inputColor) {
                return {color: inputColor}
            } else {
                const colorNode: ColorNode = {
                    type: "color",
                    inputs: {},
                    parameters: {value: cyclesColor(this.parameters.settings.color)},
                    resources: {},
                }
                return {color: [colorNode, "color"]}
            }
        }

        const invertNode: InvertNode = {
            type: "invert",
            inputs: await evaluator.getAll({color: this.parameters.inputs.color, fac: this.parameters.inputs.fac}),
            parameters: {color: cyclesColor(this.parameters.settings.color), fac: this.parameters.settings.fac},
            resources: {},
        }

        return {
            color: [invertNode, "color"],
        }
    }
}
